<script setup lang="ts">
import type { ComponentSize, StatusColor } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiSpinner - Design System Spinner/Loading Component
 *
 * A spinning indicator for brief loading states.
 *
 * WHEN TO USE SPINNER (vs Skeleton vs Progress):
 * - Brief operations (< 3 seconds): button loading, form submission
 * - When you don't know what content will appear
 * - Inline loading indicators (inside buttons, next to text)
 * - Overlay loading states
 *
 * DON'T USE SPINNER FOR:
 * - Content loading where layout is known (use Skeleton)
 * - Long operations with known progress (use Progress)
 * - Full page loads (use Skeleton for layout)
 *
 * @example
 * <UiButton :loading="isSubmitting">Save</UiButton>  // Built-in spinner
 * <UiSpinner />  // Standalone spinner
 * <UiSpinner size="lg" label="Loading..." show-label />
 */
import { computed } from 'vue';
import { IconLib } from '@/assets/icon-library';

export interface UiSpinnerProps {
   /** Size variant */
   size?: Extract<ComponentSize, 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;
   /** Spinner color */
   color?: StatusColor | 'white';
   /** Loading label (for accessibility and display) */
   label?: string;
   /** Show label text */
   showLabel?: boolean;
}

const props = withDefaults(defineProps<UiSpinnerProps>(), {
   size: 'md',
   color: 'primary',
   showLabel: false,
});

const sizeClasses = computed(() => {
   const sizes = {
      xs: 'size-4',
      sm: 'size-5',
      md: 'size-6',
      lg: 'size-8',
      xl: 'size-12',
   };
   return sizes[props.size];
});

const colorClasses = computed(() => {
   const colors: Record<StatusColor | 'white', string> = {
      primary: 'text-primary',
      secondary: 'text-secondary',
      success: 'text-green-500',
      warning: 'text-yellow-500',
      error: 'text-red-500',
      info: 'text-blue-500',
      // Spinners don't gradient cleanly; use the brand teal so a spinner
      // next to a Luma badge still reads as the same family.
      luma: 'text-primary',
      white: 'text-white',
   };
   return colors[props.color];
});

const labelSizeClasses = computed(() => {
   const sizes = {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-sm',
      lg: 'text-base',
      xl: 'text-lg',
   };
   return sizes[props.size];
});
</script>

<template>
   <div
      class="inline-flex flex-col items-center justify-center gap-2"
      role="status"
      :aria-label="label || 'Loading'"
   >
      <Icon
         :icon="IconLib.loading.spinner"
         :class="[sizeClasses, colorClasses]"
      />
      <span
         v-if="showLabel && label"
         class="text-neutral-dark" :class="[labelSizeClasses]"
      >
         {{ label }}
      </span>
      <span class="sr-only">{{ label || 'Loading' }}</span>
   </div>
</template>
